import { fetchWithChatFeedback } from './chatFeedbackRequired';
/**
 * Flow metadata API client helpers.
 *
 * Thin wrappers over the generic `PATCH /api/flows/{flowId}` endpoint for
 * editing a saved workflow's display fields (name, description) from the
 * Saved Workflows page. Mirrors the auth / error-handling conventions used by
 * the sibling `businessImpactFunctions.ts` helpers (X-User-Email header,
 * `credentials: 'include'`, friendly error parsing).
 */

import { USER_FACING_ERROR_MESSAGES } from '../utils/userFacingError';
import type { FlowMetadata } from '../types/flow';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FlowMetaPatchPayload {
  name?: string;
  description?: string;
}

/**
 * The backend echoes back the full updated flow metadata. Callers typically
 * only consume `name` / `description`, but the whole record is returned so the
 * parent can merge authoritative values (e.g. updatedAt) into local state.
 */
export interface FlowMetaPatchResponse {
  id: string;
  name?: string;
  description?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getUserEmail(): string {
  return localStorage.getItem('userEmail') || (window as any).userEmail || '';
}

function buildHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const userEmail = getUserEmail();
  if (userEmail) {
    headers['X-User-Email'] = userEmail;
  }
  return headers;
}

async function parseError(response: Response): Promise<string> {
  try {
    const data = await response.json();
    if (data?.detail && typeof data.detail === 'object' && 'error' in data.detail) {
      if (data.detail.error) return data.detail.error;
    }
    if (typeof data?.detail === 'string') return data.detail;
    if (typeof data?.error === 'string') return data.error;
    if (typeof data?.message === 'string') return data.message;
  } catch {
    /* fall through */
  }
  // Never surface raw HTTP status codes to the user — use the canonical
  // maintainers_alerted message so it's already on the allowlist by the time
  // it reaches toUserFacingError() at the JSX render boundary.
  return USER_FACING_ERROR_MESSAGES.maintainers_alerted;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/** Return the authenticated user's accessible workflows. */
export async function listAccessibleFlows(
  signal?: AbortSignal,
): Promise<FlowMetadata[]> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows`, {
    headers: buildHeaders(),
    credentials: "include",
    signal,
  });
  if (response.status === 401) {
    window.dispatchEvent(new Event("auth-expired"));
  }
  if (!response.ok) {
    throw Object.assign(new Error(await parseError(response)), { status: response.status });
  }
  const data = await response.json();
  return Array.isArray(data?.flows) ? data.flows : [];
}

/**
 * Persist an edit to a flow's display name and/or description.
 *
 * The flow's `id` is never changed by this call (the backend mutates only
 * metadata). Pass either or both fields. An empty/whitespace-only `name` is
 * rejected by the backend.
 */
export async function patchFlowMeta(
  flowId: string,
  payload: FlowMetaPatchPayload
): Promise<FlowMetaPatchResponse> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}`, {
    method: 'PATCH',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return response.json();
}
